import { Link, useLocation, useParams } from "react-router-dom";
import { SERVICES, SERVICE_MAP } from "@/data/services";
import { ArrowRight } from "@phosphor-icons/react";

// Contextual "Related Services" band that sits above the footer on every
// page, and reflects where the visitor currently is.
export default function RelatedServices() {
  const { pathname } = useLocation();
  const params = useParams();

  // Hide band on these routes (they have their own dedicated CTAs/related)
  const hideOn = ["/contact", "/free-trial"];
  if (hideOn.some((p) => pathname.startsWith(p))) return null;

  let heading = "Explore our land surveying CAD services";
  let sub =
    "Every deliverable drafted by trained CAD professionals and reviewed for closure, layering, and jurisdictional standards.";
  let items = SERVICES.slice(0, 6);

  // Service detail page: show related ones (excluding current)
  const slugMatch = pathname.match(/^\/services\/([^/]+)\/?$/);
  if (slugMatch) {
    const current = SERVICE_MAP[slugMatch[1]];
    if (current) {
      heading = `Other surveys clients pair with ${current.title}`;
      sub = "You might also need these closely related CAD deliverables.";
      items = SERVICES.filter((s) => s.slug !== current.slug).slice(0, 6);
    }
  } else if (pathname === "/services") {
    heading = "Not sure where to start?";
    sub = "Tell us about your project — we'll recommend the right scope.";
    items = SERVICES.slice(0, 6);
  } else if (pathname === "/about") {
    heading = "What we draft";
    sub = "13 focused land surveying CAD services, delivered across all 50 states.";
    items = SERVICES.slice(0, 6);
  } else if (pathname === "/process") {
    heading = "Apply our process to any survey type";
    sub = "Our 4-step workflow scales across every deliverable below.";
    items = SERVICES.slice(0, 6);
  }

  return (
    <section
      className="bg-white border-t border-zinc-200"
      data-testid="related-services-band"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div className="max-w-2xl">
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#0044FF] mb-3">
              Related Services
            </div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight text-zinc-950">
              {heading}
            </h2>
            <p className="mt-3 text-zinc-600 max-w-xl">{sub}</p>
          </div>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 px-4 py-2 border border-zinc-900 text-zinc-950 text-sm font-medium btn-lift hover:bg-zinc-900 hover:text-white self-start"
            data-testid="related-view-all"
          >
            View all services <ArrowRight size={14} weight="bold" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
          {items.map((s, idx) => (
            <Link
              key={s.slug}
              to={`/services/${s.slug}`}
              className="group bg-white p-6 md:p-7 hover:bg-zinc-50 transition-colors"
              data-testid={`related-card-${s.slug}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-mono uppercase tracking-[0.25em] text-zinc-400">
                  FIG {String(idx + 1).padStart(2, "0")}
                </span>
                <ArrowRight
                  size={16}
                  weight="bold"
                  className="text-zinc-300 group-hover:text-[#0044FF] group-hover:translate-x-1 transition-all"
                />
              </div>
              <h3 className="mt-4 font-display text-xl md:text-2xl text-zinc-950 tracking-tight leading-snug">
                {s.title}
              </h3>
              <p className="mt-2 text-sm text-zinc-600 leading-relaxed">
                {s.short}
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
